/**
 * Per Ankh — the browser half of push notifications.
 *
 * getPushState() says what the on/off switch should show; enablePush() asks the member's
 * permission, subscribes this device and tells the server; disablePush() undoes it.
 * Nothing here runs until the member taps the switch.
 *
 * iPhones only allow push for a site added to the Home Screen, so there the state is
 * 'ios-install' and the panel explains how, instead of a switch that cannot work.
 */

export const PUSH_WORKER = '/push-sw.js';

export function urlBase64ToUint8Array(b64) {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}
function isStandalone() {
  return window.matchMedia?.('(display-mode: standalone)').matches || navigator.standalone === true;
}
function canPush() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.getRegistration(PUSH_WORKER) || await navigator.serviceWorker.getRegistration('/');
  return reg ? reg.pushManager.getSubscription() : null;
}

/** 'unsupported' | 'ios-install' | 'denied' | 'on' | 'off' | 'unavailable' (server has no keys) */
export async function getPushState(headers = async () => ({})) {
  if (!canPush()) return isIos() && !isStandalone() ? 'ios-install' : 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    if (Notification.permission === 'granted' && await currentSubscription()) return 'on';
    const r = await fetch('/api/push/key', { headers: await headers() });
    if (!r.ok) return 'unavailable';
    const { key } = await r.json();
    return key ? 'off' : 'unavailable';
  } catch { return 'unavailable'; }
}

export async function enablePush(headers = async () => ({})) {
  if (!canPush()) throw new Error('This browser cannot show notifications.');
  const kr = await fetch('/api/push/key', { headers: await headers() });
  const { key } = kr.ok ? await kr.json() : {};
  if (!key) throw new Error('Notifications are not switched on for the site yet.');
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notifications were not allowed on this device.');
  const reg = await navigator.serviceWorker.register(PUSH_WORKER, { scope: '/' });
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
  const r = await fetch('/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await headers()) },
    body: JSON.stringify({ subscription: sub.toJSON() }),
  });
  if (!r.ok) throw new Error('Could not save this device. Please try again.');
  return true;
}

export async function disablePush(headers = async () => ({})) {
  const sub = await currentSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  try {
    await fetch('/api/push/unsubscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await headers()) },
      body: JSON.stringify({ endpoint }),
    });
  } catch { /* the server drops dead endpoints on its own */ }
  await sub.unsubscribe();
  return true;
}

/** The member's notification choices: { messages, comments, preview, quietEnabled, quietStart, quietEnd, timeZone }. */
export async function loadPrefs(headers = async () => ({})) {
  const r = await fetch('/api/push/prefs', { headers: await headers() });
  if (!r.ok) throw new Error('Could not load your notification settings.');
  return (await r.json()).prefs;
}

/** Save some of the choices. This device's time zone goes along, so quiet hours mean local time. */
export async function savePrefs(headers = async () => ({}), patch = {}) {
  let timeZone;
  try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* the server uses UTC */ }
  const r = await fetch('/api/push/prefs', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...(await headers()) },
    body: JSON.stringify({ ...patch, ...(timeZone ? { timeZone } : {}) }),
  });
  if (!r.ok) throw new Error('Could not save that. Please try again.');
  return (await r.json()).prefs;
}

/** Make the site installable: adds <link rel="manifest"> and the iPhone icon once. */
export function addManifestLink() {
  if (document.querySelector('link[rel="manifest"]')) return;
  const m = document.createElement('link');
  m.rel = 'manifest'; m.href = '/manifest.webmanifest';
  document.head.appendChild(m);
  if (!document.querySelector('link[rel="apple-touch-icon"]')) {
    const a = document.createElement('link');
    a.rel = 'apple-touch-icon'; a.href = '/assets/images/app-icons/apple-touch-icon.png';
    document.head.appendChild(a);
  }
}
